import * as React from 'react';
import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/kpi-card';
import { PresenceChart } from '@/components/presence-chart';
import { AttendanceChart } from '@/components/attendance-chart';
import { useStore } from '@/lib/store-context';
import {
  listEmployees, getAttendanceDaily, getEmployee,
  type EmployeeSummary,
} from '@/lib/hr-api';
import { getModuleStats } from '@/lib/modules-api';
import { fmtINR, fmtNumber } from '@/lib/format';

const Search = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  empId: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_app/hr')({
  validateSearch: Search,
  component: Page,
});

function Page() {
  const matches = useMatches();
  const onChild = matches.some((m) => m.routeId.startsWith('/_app/hr/'));
  if (onChild) return <Outlet />;
  return <Landing />;
}

function Landing() {
  const { selected: store } = useStore();
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const status = search.status ?? 'active';
  const q = search.q ?? '';
  const selectedId = search.empId;

  const [draft, setDraft] = React.useState(q);
  React.useEffect(() => setDraft(q), [q]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      if (draft !== q) nav({ to: '.', search: (prev) => ({ ...prev, q: draft || undefined }), replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [draft, q, nav]);

  const stats = useQuery({ queryKey: ['modules:stats'], queryFn: getModuleStats, staleTime: 5 * 60_000 });
  const dirQ  = useQuery({
    queryKey: ['hr:employees', store, q, status],
    queryFn: () => listEmployees({ q, status, store }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const presenceQ = useQuery({
    queryKey: ['hr:attendance:daily', store, 30],
    queryFn: () => getAttendanceDaily({ days: 30, store }),
    staleTime: 60_000,
    enabled: !selectedId,
  });
  const empQ = useQuery({
    queryKey: ['hr:employee', store, selectedId],
    queryFn: () => getEmployee({ id: selectedId!, store }),
    enabled: !!selectedId,
    staleTime: 60_000,
  });

  const select   = (id: number | null) => nav({ to: '.', search: (prev) => ({ ...prev, empId: id ?? undefined }), replace: true });
  const selected = selectedId ? dirQ.data?.employees.find((e) => e.id === selectedId) : undefined;
  const t = dirQ.data?.totals;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Active"          value={t ? fmtNumber(t.active) : (stats.data ? fmtNumber(stats.data.hr.employees) : '—')} />
        <KpiCard label="Discontinued"    value={t ? fmtNumber(t.inactive) : '—'} />
        <KpiCard label="Total"           value={t ? fmtNumber(t.total) : '—'} hint="Including history" />
        <KpiCard label="Advance Balance" value={t ? fmtINR(t.advanceBalance) : '—'} hint="Net debit − credit" />
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>
            {selected ? `${selected.name || `EMPID ${selected.empId}`} · attendance` : 'Daily attendance · last 30 days'}
          </CardTitle>
          {selected && (
            <div className="flex items-center gap-2 text-sm">
              <Button asChild variant="outline" size="sm">
                <Link to="/hr/employees/$id" params={{ id: String(selected.id) }} search={(prev) => prev}>
                  Open profile →
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => select(null)}>
                <X className="mr-1 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!selectedId && (
            <>
              {presenceQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
              {presenceQ.error && <p className="text-sm text-destructive">{(presenceQ.error as Error).message}</p>}
              {presenceQ.data && (
                <PresenceChart days={presenceQ.data.days} activeCount={presenceQ.data.activeCount} />
              )}
            </>
          )}
          {selectedId && (
            <>
              {empQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
              {empQ.error && <p className="text-sm text-destructive">{(empQ.error as Error).message}</p>}
              {empQ.data && (
                empQ.data.attendance.length === 0
                  ? <p className="text-sm text-muted-foreground">No attendance logs for this employee.</p>
                  : <AttendanceChart
                      days={empQ.data.attendance}
                      shiftStartMin={hmToMin(empQ.data.employee.inTime)}
                      shiftEndMin={hmToMin(empQ.data.employee.outTime)}
                    />
              )}
            </>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            {selectedId ? 'Tap any row below to switch employee, or Clear to see store-wide.' : 'Tap any employee row to filter the chart.'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle>Employees</CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Search name, EMPID, mobile…"
              className="h-9 w-full sm:w-64"
            />
            <div className="flex items-center gap-1 text-xs">
              {(['active', 'inactive', 'all'] as const).map((s) => (
                <Button
                  key={s}
                  variant={status === s ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 capitalize sm:flex-initial"
                  onClick={() => nav({ to: '.', search: (prev) => ({ ...prev, status: s }), replace: true })}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {dirQ.isLoading && <p>Loading…</p>}
          {dirQ.error && <p className="text-sm text-destructive">{(dirQ.error as Error).message}</p>}
          {dirQ.data && (
            <>
              {/* Mobile: card list */}
              <div className="space-y-2 md:hidden">
                {dirQ.data.employees.length === 0 && (
                  <p className="py-6 text-center text-sm text-muted-foreground">No employees match.</p>
                )}
                {dirQ.data.employees.map((e) => (
                  <EmployeeMobileCard key={e.id} e={e} selected={e.id === selectedId} onSelect={() => select(e.id)} />
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block">
                <Table>
                  <THead>
                    <TR>
                      <TH>EMPID</TH>
                      <TH>Name</TH>
                      <TH className="hidden lg:table-cell">Type</TH>
                      <TH>Mobile</TH>
                      <TH className="text-right">Salary</TH>
                      <TH className="hidden lg:table-cell text-right">Comm %</TH>
                      <TH className="hidden xl:table-cell">In/Out</TH>
                      <TH className="text-right">Adv. Bal</TH>
                      <TH>Status</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {dirQ.data.employees.map((e) => {
                      const isSel = e.id === selectedId;
                      return (
                        <TR
                          key={e.id}
                          className={`cursor-pointer ${isSel ? 'bg-emerald-50 hover:bg-emerald-100' : ''}`}
                          tabIndex={0}
                          role="button"
                          aria-pressed={isSel}
                          aria-label={`Filter chart by ${e.name || `EMPID ${e.empId}`}`}
                          onClick={() => select(isSel ? null : e.id)}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); select(isSel ? null : e.id); }
                          }}
                        >
                          <TD className="font-mono">{e.empId}</TD>
                          <TD>{e.name || '—'}</TD>
                          <TD className="hidden lg:table-cell">{e.empTypeLabel || '—'}</TD>
                          <TD className="font-mono">{e.mobile || '—'}</TD>
                          <TD className="text-right">{fmtINR(e.salary)}</TD>
                          <TD className="hidden lg:table-cell text-right">{e.commPercent ? `${e.commPercent}%` : '—'}</TD>
                          <TD className="hidden xl:table-cell text-xs text-muted-foreground">
                            {e.inTime || '—'} – {e.outTime || '—'}
                          </TD>
                          <TD className={`text-right ${e.advance.balance > 0 ? 'text-amber-700' : ''}`}>
                            {e.advance.balance ? fmtINR(e.advance.balance) : '—'}
                          </TD>
                          <TD>
                            {e.active
                              ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">Active</span>
                              : <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Discontinued</span>
                            }
                          </TD>
                        </TR>
                      );
                    })}
                    {dirQ.data.employees.length === 0 && (
                      <TR><TD colSpan={9} className="py-6 text-center text-sm text-muted-foreground">No employees match.</TD></TR>
                    )}
                  </TBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmployeeMobileCard({ e, selected, onSelect }: { e: EmployeeSummary; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`block w-full rounded-lg border p-3 text-left active:bg-muted ${selected ? 'border-emerald-500 bg-emerald-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{e.name || '—'}</div>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">EMPID {e.empId}</span>
            {e.mobile ? <> · <span className="font-mono">{e.mobile}</span></> : null}
          </div>
        </div>
        {e.active
          ? <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">Active</span>
          : <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Disc.</span>
        }
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Salary <span className="text-foreground">{fmtINR(e.salary)}</span></span>
        {e.commPercent ? <span>Comm <span className="text-foreground">{e.commPercent}%</span></span> : null}
        {e.advance.balance !== 0 && (
          <span>Adv. <span className={e.advance.balance > 0 ? 'text-amber-700' : 'text-foreground'}>{fmtINR(e.advance.balance)}</span></span>
        )}
      </div>
    </button>
  );
}

function hmToMin(s: string): number | undefined {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  return m ? Number(m[1]) * 60 + Number(m[2]) : undefined;
}
